import { createFetch } from '@vueuse/core'
import { defineStore } from 'pinia'

import { API_BASE_URL } from '@/libs/apiBase'
import { eventData, splitEvents, type ISseEvent } from '@/libs/sse'
import router from '@/router'
import { useAppStore } from '@/stores/app'

/*
 * The API's base path (`libs/apiBase.ts`). Exported here too because not everything that
 * reaches the API is a fetch: a media file is shown by an <img src> / <video src>,
 * authenticated by the JWT cookie.
 */
export { API_BASE_URL }

export const useFetchStore = defineStore('fetch', () => {
    const appStore = useAppStore()

    const fetch = createFetch({
        baseUrl: API_BASE_URL,
        options: {
            /*
             * The service says why it refused — { error: { code, message } } — and useFetch
             * drops the body of a failed response unless told to keep it. Without this every
             * refusal would read "an error occurred" while the code sat unread.
             */
            updateDataOnError: true,
            beforeFetch({ options }) {
                const headers = new Headers(options.headers)
                const jwt     = appStore.getJWT()

                /*
                 * A caller that states its own credentials keeps them: a session open in the
                 * same browser must not replace them with the person's JWT. (The screen player
                 * has its own fetch in tv-display.)
                 */
                if (jwt && !headers.has('Authorization')) headers.set('Authorization', `Bearer ${jwt}`)
                options.headers = headers

                return { options }
            },
            async onFetchError(ctx) {
                /*
                 * An expired or revoked session. The login form's own wrong password answers
                 * 401 too, and must stay on the form with its message — so only a session
                 * that exists is thrown away and sent to sign in again. A PUBLIC page is never
                 * sent to the login: its 401 is not the person's session.
                 */
                const current = router.currentRoute.value

                if (ctx.response?.status === 401 && appStore.isLoggedIn() && current.meta.requiresAuth !== false) {
                    appStore.setLogout()
                    await router.push({
                        name:  'login',
                        query: current.name && current.name !== 'login' ? { redirect: current.fullPath } : {},
                    })
                }

                return ctx
            },
        },
    })

    /**
     * A POST whose answer is Server-Sent Events in the response body (Milo's answer stream,
     * docs/API.md §14.5) — `createFetch` buffers the whole body, so this reads it piece by
     * piece and hands each `event:` / `data:` block to `onEvent` as it arrives. A refusal
     * before the stream starts is an ordinary JSON error answer: `status` and `data` carry it.
     * `signal` stops reading (the drawer closed on a new conversation).
     */
    const stream = async (
        path: string,
        body: unknown,
        onEvent: (name: string, data: unknown) => void,
        signal?: AbortSignal,
    ): Promise<{ status: number, data: unknown }> => {
        const headers = new Headers({ 'Content-Type': 'application/json', Accept: 'text/event-stream' })
        const jwt     = appStore.getJWT()

        if (jwt) headers.set('Authorization', `Bearer ${jwt}`)

        let response: Response

        try {
            response = await window.fetch(`${API_BASE_URL}${path}`, { method: 'POST', headers, body: JSON.stringify(body), signal })
        } catch {
            return { status: 0, data: null }
        }

        const type = response.headers.get('Content-Type') ?? ''

        if (!response.ok || !type.includes('text/event-stream') || !response.body) {
            let data: unknown = null

            try {
                data = await response.json()
            } catch {
                data = null
            }

            if (response.status === 401 && appStore.isLoggedIn()) {
                appStore.setLogout()
                await router.push({ name: 'login' })
            }

            return { status: response.status, data }
        }

        const reader  = response.body.getReader()
        const decoder = new TextDecoder()
        let rest      = ''

        // Parsed first, handed over outside the try: a fault in the page's handler is not a
        // block that "was not JSON" and must not disappear silently.
        const hand = (events: ISseEvent[]) => {
            for (const event of events) {
                const data = eventData<unknown>(event)

                if (data !== null) onEvent(event.event, data)
            }
        }

        try {
            for (;;) {
                const { done, value } = await reader.read()

                if (done) break

                const parsed = splitEvents(rest + decoder.decode(value, { stream: true }))

                rest = parsed.rest
                hand(parsed.events)
            }

            if (rest.trim()) hand(splitEvents(`${rest}\n\n`).events)
        } catch (error) {
            if (signal?.aborted) return { status: 499, data: null }
            if (error instanceof TypeError || error instanceof DOMException) return { status: 0, data: null }

            throw error
        }

        return { status: response.status, data: null }
    }

    return { fetch, stream }
})
